import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useBusiness } from './BusinessContext';
import { isCoreMessagesEnabled, fetchCoreUnreadCounts } from '../lib/messagesCore';

// Define the context type
interface UnreadMessagesContextType {
  unreadCount: number;
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  fetchUnreadCount: () => Promise<void>;
}

// Create the context with default values
const UnreadMessagesContext = createContext<UnreadMessagesContextType>({
  unreadCount: 0,
  setUnreadCount: () => {},
  fetchUnreadCount: async () => {},
});

// Create a provider component
export const UnreadMessagesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { selectedBusiness } = useBusiness();
  
  // Function to fetch the unread message count (including unviewed missed calls)
  // Excludes muted email senders from the count
  const fetchUnreadCount = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      // Core tables: one call returns the totals.
      if (await isCoreMessagesEnabled(selectedBusiness.id)) {
        const counts = await fetchCoreUnreadCounts(selectedBusiness.id);
        setUnreadCount(counts.unread + counts.unreadSuppliers);
        return;
      }

      // First, get muted email senders for this business
      const { data: mutedSenders, error: mutedError } = await supabase
        .from('muted_email_senders')
        .select('email_address')
        .eq('business_id', selectedBusiness.id);
      
      if (mutedError) {
        console.error('Error fetching muted senders:', mutedError);
      }
      
      const mutedEmails = new Set((mutedSenders || []).map(m => m.email_address.toLowerCase().trim()));
      
      // Get all unread messages (not just count, so we can filter)
      const { data: unreadMessages, error: messagesError } = await supabase
        .from('all_messages')
        .select('id, message_type, from_number')
        .eq('business_id', selectedBusiness.id)
        .eq('read', 'unread')
        .eq('direction', 'inbound');
      
      if (messagesError) {
        console.error('Error fetching unread message count:', messagesError);
        return;
      }

      // Filter out messages from muted email senders
      const nonMutedMessages = (unreadMessages || []).filter(msg => {
        if (msg.message_type !== 'email') return true; // Include non-email messages
        // For emails, check if sender is muted
        const senderEmail = (msg.from_number || '').toLowerCase().trim();
        // Extract email from "Name <email>" format
        const emailMatch = senderEmail.match(/<([^>]+)>/);
        const normalizedEmail = emailMatch ? emailMatch[1] : senderEmail;
        return !mutedEmails.has(normalizedEmail);
      });
      
      const messagesCount = nonMutedMessages.length;

      // Get unviewed missed calls count
      const { count: callsCount, error: callsError } = await supabase
        .from('call_history')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', selectedBusiness.id)
        .eq('direction', 'inbound')
        .eq('status', 'missed')
        .eq('viewed', false);
      
      if (callsError) {
        console.error('Error fetching unviewed missed calls count:', callsError);
        return;
      }

      // Unviewed pending AI receptionist callback requests
      const { count: callbacksCount, error: callbacksError } = await supabase
        .from('agent_callbacks')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'pending')
        .eq('viewed', false);

      if (callbacksError) {
        console.error('Error fetching unviewed callbacks count:', callbacksError);
      }

      const totalCount = messagesCount + (callsCount || 0) + (callbacksCount || 0);
      
      setUnreadCount(totalCount);
    } catch (error) {
      console.error('Error in fetchUnreadCount:', error);
    }
  };
  
  // Set up polling and subscription for unread count when business changes
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;
    
    // Fetch immediately on load
    fetchUnreadCount();
    
    // Set up polling with adaptive interval based on connection quality
    // Realtime subscription handles most updates, polling is just a fallback
    // Base interval: 60 seconds, but adapts to connection quality
    const getPollingInterval = () => {
      try {
        const { connectionQualityDetector } = require('../lib/connectionQuality');
        return connectionQualityDetector.getRecommendedPollingInterval(60000);
      } catch {
        // Fallback if connection detector not available
        return 60000;
      }
    };
    
    const pollingInterval = getPollingInterval();
    const intervalId = setInterval(fetchUnreadCount, pollingInterval);
    
    // Subscribe to message changes using Supabase's realtime functionality
    const subscription = supabase
      .channel('messages_and_calls_changes')
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'all_messages',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        (payload) => {
          console.log('📨 New message inserted:', payload);
          fetchUnreadCount();
        }
      )
      .on('postgres_changes', 
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'all_messages',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        (payload) => {
          console.log('📨 Message updated:', payload);
          fetchUnreadCount();
        }
      )
      .on('postgres_changes', 
        { 
          event: 'DELETE', 
          schema: 'public', 
          table: 'all_messages',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        (payload) => {
          console.log('📨 Message deleted:', payload);
          fetchUnreadCount();
        }
      )
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'call_history',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        (payload) => {
          console.log('📞 Call history changed for badge:', payload);
          fetchUnreadCount();
        }
      )
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'agent_callbacks',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        (payload) => {
          console.log('📲 Agent callbacks changed for badge:', payload);
          fetchUnreadCount();
        }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'sms_messages', filter: `business_id=eq.${selectedBusiness.id}` },
        () => { fetchUnreadCount(); }
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'email_message_state', filter: `business_id=eq.${selectedBusiness.id}` },
        () => { fetchUnreadCount(); }
      )
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'muted_email_senders',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        (payload) => {
          console.log('🔇 Muted senders changed for badge:', payload);
          fetchUnreadCount(); // Recalculate count when muted senders change
        }
      )
      .subscribe();
    
    // Clean up on unmount or when business changes
    return () => {
      clearInterval(intervalId);
      if (supabase) {
        supabase.removeChannel(subscription);
      }
    };
  }, [selectedBusiness?.id]);
  
  return (
    <UnreadMessagesContext.Provider value={{ unreadCount, setUnreadCount, fetchUnreadCount }}>
      {children}
    </UnreadMessagesContext.Provider>
  );
};

// Custom hook to use the unread messages context
export const useUnreadMessages = () => useContext(UnreadMessagesContext); 